import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDate, formatEuro } from '@rental/i18n'
import { cn } from '@rental/ui'
import { Link } from '@tanstack/react-router'
import { can } from '../access'
import { useAccess } from '../access-context'
import { ListRow, ROW_LINK } from '../components/Results'
import { StatusChip } from '../components/StatusChip'
import { useLocale } from '../i18n'
import { BookingCode } from '../reports/BookingCode'
import { statusTone } from './fines'

/**
 * CUS-4 and the booking page (S6): the fines its rentals were matched to,
 * newest first, with where each stands and the fee charged for it. A link to
 * the fine for the holders of fines.manage; the words alone for anyone else.
 * Rows of their list as every list's (UI-2g): the notice first, its status at
 * the end of that line, the booking and the fee under it; the booking's code a
 * link of its own, above the row's.
 */
export function FineMentions({
  fines,
  withCode,
}: {
  fines: readonly ApiSchemas['StaffFineMention'][]
  /** On a customer's record: which booking each one was. */
  withCode: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const manage = can(access, 'fines.manage')
  return (
    <ul className="flex flex-col" data-testid="fine-mentions">
      {fines.map((fine) => {
        const line = format(m.fines.mentions.line, {
          authority: fine.authority,
          number: fine.noticeNumber,
          date: formatDate(locale, fine.violatedOn, 'short'),
          time: fine.violatedAt,
          plate: fine.plate,
        })
        const extra = withCode || fine.feeCents !== null
        return (
          <ListRow
            key={fine.id}
            still={!manage}
            title={
              manage ? (
                <Link
                  to="/fines/$fineId"
                  params={{ fineId: fine.id }}
                  className={cn('font-medium', ROW_LINK)}
                >
                  {line}
                </Link>
              ) : (
                <span className="font-medium">{line}</span>
              )
            }
            end={
              <StatusChip tone={statusTone(fine.status)}>{m.fines.status[fine.status]}</StatusChip>
            }
          >
            {extra ? (
              <>
                {withCode ? (
                  // A link of its own: positioned, it stands above the row's.
                  <span className="relative">
                    <BookingCode code={fine.code} />
                  </span>
                ) : null}
                {fine.feeCents !== null ? (
                  <span className="figures">
                    {format(m.fines.mentions.fee, { amount: formatEuro(fine.feeCents, locale) })}
                  </span>
                ) : null}
              </>
            ) : null}
          </ListRow>
        )
      })}
    </ul>
  )
}
